/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

/**
 * A function used to resolve message objects from arbitrary sources given a locale string. It will be called multiple
 * times with decreasingly specific versions of the original locale that was passed to `resolveMessages`. For example:
 * @example
 * i18n.resolveMessages('fr-FR-foo',
 *   locale => { console.log('calling with %s', locale); return {}; }
 * )
 * // output is:
 * // calling with fr-FR-foo
 * // calling with fr-FR
 * // calling with fr
 * @callback resolveMessages~resolvableFn
 * @param {string} the current locale string
 * @return {Object} the object containing messages matching the locale
 */

/**
 * Given a valid BCP-47 locale string and a variable argument of functions to resolve messages, merge and return *ALL*
 * appropriate messages that the application should load.
 *
 * This can include multiple sources of messages, as well as both client-side or server-side storage of the messages.
 * This will also handle overrides correctly, in the case of differential translations between a basic language
 * (`zh` for example) and its more specific translations (`zh-TW` exists and provides only some overrides of certain
 * messages in `zh`).
 *
 * See {@link resolveMessages~resolvableFn} for the signature and purpose of the functions you can provide here.
 *
 * For more details and examples, refer to {@link https://ui.dev.oclc.org/guides/i18n.html}
 *
 * @param {string} locale the original BCP-47 locale we want to find all applicable messages
 * @param {...resolveMessages~resolvableFn} resolvableFns functions to use to resolve messages matching the locale
 *
 * @returns {Object} the full messages object from aggregating all matching messages
 */
function resolveMessages(locale, ...resolvableFns) {
  let messages = {};

  console.groupCollapsed &&
    console.groupCollapsed(
      'resolveMessages() diagnostics (errors may be expected)',
    );

  resolvableFns.forEach((resolveMessagesFn) => {
    messages = Object.assign(messages, loadMessages(locale, resolveMessagesFn));
  });

  console.groupEnd && console.groupEnd();
  return messages;
}

/**
 * Resolves the locale based on the language tag found in the HTML lang attribute.
 * Expect a well-formed BCP-47 language tag, which may include optional sub-tags.
 * If the locale is not "truthy", we resort to a default.
 *
 * @returns {string} the resolved locale
 */
function resolveLocale() {
  return document.documentElement.lang || 'en';
}

/**
 * Given a BCP-47 locale string, determine what language it refers to.
 *
 * @param {string} locale a BCP-47 locale string
 *
 * @returns {string} just the language part of the string
 */
function resolveLanguage(locale) {
  return locale.split('-')[0];
}

/**
 * Given a BCP-47 locale string, load and return the matching messages. This will use a fallback algorithm
 * to handle merging partial language translations.
 *
 * @param {string} locale a BCP-47 locale string, like en-US or zh-Hans-CN
 * @param {resolveMessages~resolvableFn} resolveMessagesFn the provided function to call in order to fetch matching messages
 *
 * @returns {Object} the messages object
 */
function loadMessages(locale, resolveMessagesFn) {
  let reducedMessages = {};
  let myMessages;

  // if have more subtags we can peel off, do so and try again
  if (reduceLocaleTag(locale) !== locale) {
    reducedMessages = loadMessages(reduceLocaleTag(locale), resolveMessagesFn);
  }

  // tail-recursion so that more specific messages overwrite less
  myMessages = resolveSafely(locale, resolveMessagesFn);

  return Object.assign(reducedMessages, myMessages);
}

/**
 * Given a BCP-47 locale string, reduce it by slicing off the current most specific subtag on it. This will gradually
 * return a less specific locale string; for example, this will transform az-Arab-IR -> az-Arab -> az over subsequent
 * calls. If the string is already as reduced as possible, it will simply return that string.
 *
 * @param {string} locale a locale string potentially containing subtags to
 *
 * @returns {string} the locale string with the last subtag removed, or that string if already reduced
 */
function reduceLocaleTag(locale) {
  if (locale.lastIndexOf('-') > 0) {
    return locale.slice(0, locale.lastIndexOf('-'));
  } else {
    return locale;
  }
}

/**
 * Safely attempt to call a {@link resolveMessages~resolvableFn} function and return its results. If the provided
 * function throws an error, it will be handled here.
 *
 * @param {string} locale the BCP-47 locale to check
 * @param {resolveMessages~resolvableFn} resolveMessagesFn the function to call that might actually resolve the locale
 * argument's messages
 *
 * @returns {Object} the messages resolved by resolveMessagesFn, or null if not available
 */
function resolveSafely(locale, resolveMessagesFn) {
  try {
    return resolveMessagesFn(locale);
  } catch (e) {
    console.log("No messages found for locale '%s':", locale, e);
    return null;
  }
}

export { resolveLocale, resolveLanguage, resolveMessages };
